// Direct & indirect objects — telling them apart, the two pronoun sets
// (o/a/os/as vs lhe/lhes), how they fuse (lhe + o → lho), and how to add
// emphasis with a + pronoun. A side-by-side pronoun table closes the page.
import { renderGuideSections } from "../ui/guideSections.js";

export function objectsView(app) {
  const table = `
    <div class="bg-white rounded-2xl border border-sage-100 p-5 mt-6">
      <h3 class="text-lg font-bold text-sage-800 mb-3">Object Pronouns at a Glance</h3>
      <div class="overflow-x-auto"><table class="w-full text-sm">
        <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
          <th class="py-2 pr-3">Person</th>
          <th class="py-2 px-3">Direct (what)</th>
          <th class="py-2 px-3">Indirect (to/for whom)</th>
        </tr></thead>
        <tbody>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">me</td><td class="py-2 px-3">me</td><td class="py-2 px-3">me</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">you (inf.)</td><td class="py-2 px-3">te</td><td class="py-2 px-3">te</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">him / it</td><td class="py-2 px-3">o</td><td class="py-2 px-3">lhe</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">her / it</td><td class="py-2 px-3">a</td><td class="py-2 px-3">lhe</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">us</td><td class="py-2 px-3">nos</td><td class="py-2 px-3">nos</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">you all</td><td class="py-2 px-3">vos</td><td class="py-2 px-3">vos</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">them (m.) / you all</td><td class="py-2 px-3">os</td><td class="py-2 px-3">lhes</td></tr>
          <tr><td class="py-2 pr-3 font-medium">them (f.) / you all</td><td class="py-2 px-3">as</td><td class="py-2 px-3">lhes</td></tr>
        </tbody>
      </table></div>
      <p class="text-sage-500 text-xs mt-3">Only the third person differs (o/a/os/as vs lhe/lhes). When both appear, they <b>fuse</b>: <i>lhe + o → lho</i>, <i>me + o → mo</i>: <i>Dou-lho.</i></p>
    </div>`;

  renderGuideSections(
    app,
    "Direct & Indirect Objects",
    "Most verbs act on something. The direct object is what the action lands on; the indirect object is who benefits from it. Portuguese marks the two with different pronouns — and the difference only really shows up in the third person.",
    [
      {
        title: "1. Spotting which is which",
        body: `
          <p class="text-sage-700 mb-2">Ask two questions about the verb:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5 mb-2">
            <li><b>What / whom?</b> → direct object. <i>Compro <u>o pão</u>.</i> (I buy the bread.)</li>
            <li><b>To whom / for whom?</b> → indirect object. <i>Compro o pão <u>para a minha mãe</u>.</i></li>
          </ul>
          <p class="text-sage-700 text-sm">A sentence can have both: <i>Compro-lhe o pão</i> — <i>o pão</i> (direct) and <i>lhe</i> = for her (indirect).</p>
        `,
      },
      {
        title: "2. Replacing the noun with a pronoun",
        body: `
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>Direct:</b> <i>Vejo <u>o filme</u> → Vejo-<b>o</b>.</i></li>
            <li><b>Indirect:</b> <i>Falo <u>ao João</u> → Falo-<b>lhe</b>.</i></li>
            <li>By default the pronoun comes <b>after</b> the verb (<i>Vejo-o</i>), but it moves <b>before</b> after a negative or words like <i>que</i> (<i>não o vejo</i>, <i>que o viu</i>).</li>
          </ul>
        `,
      },
      {
        title: "3. Combining both — me+o→mo, lhe+o→lho",
        body: `
          <p class="text-sage-700 mb-2">When you use both pronouns, the indirect comes first and <b>fuses</b> with the direct:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>me + o → <b>mo</b></i>: <i>Dás o livro a mim → Dá-<b>mo</b>.</i></li>
            <li><i>lhe + o → <b>lho</b></i>: <i>Dou o livro à Maria → Dou-<b>lho</b>.</i></li>
            <li><i>lhes + as → <b>lhas</b></i>: <i>Mandei as fotos a eles → Mandei-<b>lhas</b>.</i></li>
          </ul>
        `,
      },
      {
        title: "4. Emphasis and clarity",
        body: `
          <p class="text-sage-700 mb-2">Because <b>lhe</b> and <b>lhes</b> don't show gender, you can add <i>a + a stressed pronoun</i> to make the person clear or emphatic:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>Dei-<b>lhe</b> o presente <b>a ela</b>.</i> (I gave the present to her.)</li>
            <li><i>Disse-<b>lhes</b> a verdade <b>a eles</b>.</i></li>
          </ul>
          <p class="text-sage-700 text-sm mt-3"><b>Heads-up:</b> in everyday European Portuguese, speakers often replace the clitic with <i>a + pronoun</i> entirely — <i>Dei o presente <b>a ela</b></i> — since the fused forms (<i>lho, lha</i>) sound more formal or written.</p>
        `,
      },
    ],
    "",
    table
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
